import { Button, Input } from "@/components/ui";
import type {
  AttorneySummaryFilters,
  AttorneySummaryResponse,
} from "@/lib/data/requests/reports/reports-attorney-summary";
import { ReportTable } from "../shared/ReportTable";
import { attorneyColumns } from "./AttorneyColumns";
import { ReportRoleDropdown } from "../shared/ReportRoleDropdown";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";

type Props = {
  filters: AttorneySummaryFilters;
  report?: AttorneySummaryResponse;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isPinning: boolean;
  onPinRole: (roleId: string | null) => void;
  onFilters: (filters: AttorneySummaryFilters) => void;
  onReset: () => void;
  onRetry: () => void;
};
const periods = [
  ["month-to-date", "Month to date"],
  ["prior-month", "Prior month"],
  ["week-to-date", "Week to date"],
  ["prior-week", "Prior week"],
] as const;
const litigation = [
  ["all", "Both"],
  ["litigation", "Yes"],
  ["non-litigation", "No"],
] as const;
export function AttorneySummaryReport({
  filters,
  report,
  isLoading,
  isFetching,
  isError,
  isPinning,
  onPinRole,
  onFilters,
  onReset,
  onRetry,
}: Props) {
  const data = report?.data;
  const selectedRoleId = filters.roleId
    ? data?.roles.find((item) => item.id === filters.roleId)?.id
    : (data?.selectedRoleId ?? undefined);
  const role = data?.roles.find((r) => r.id === selectedRoleId);
  const update = (changes: Partial<AttorneySummaryFilters>) => {
    onFilters({ ...filters, ...changes });
  };
  return (
    <div className="space-y-6" aria-busy={isFetching}>
      <div className="flex items-baseline justify-between gap-3">
        <h1 className="text-3xl font-semibold tracking-tight">Attorneys</h1>
        <span className="text-muted-foreground min-h-4 text-xs">
          {isFetching ? "Updating report…" : ""}
        </span>
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <fieldset className="space-y-1.5">
          <legend className="text-muted-foreground mb-1.5 text-xs font-semibold tracking-wide uppercase">
            Date range
          </legend>
          <div className="flex flex-wrap gap-1.5">
            {periods.map(([value, label]) => (
              <Button
                key={value}
                variant={filters.period === value ? "default" : "outline"}
                className="h-10 rounded-md px-3"
                aria-pressed={filters.period === value}
                onClick={() => {
                  update({ period: value });
                }}
              >
                {label}
              </Button>
            ))}
          </div>
        </fieldset>
        <fieldset className="space-y-1.5">
          <legend className="text-muted-foreground mb-1.5 text-xs font-semibold tracking-wide uppercase">
            Litigation history
          </legend>
          <div className="flex gap-1.5">
            {litigation.map(([value, label]) => (
              <Button
                key={value}
                variant={filters.litigation === value ? "default" : "outline"}
                className="h-10 rounded-md px-3"
                aria-pressed={filters.litigation === value}
                onClick={() => {
                  update({ litigation: value });
                }}
              >
                {label}
              </Button>
            ))}
          </div>
        </fieldset>
        <div className="w-40 space-y-1.5">
          <label
            htmlFor="attorneys-as-of"
            className="text-muted-foreground text-xs font-semibold tracking-wide uppercase"
          >
            As of date
          </label>
          <Input
            id="attorneys-as-of"
            type="date"
            min="0002-01-01"
            value={filters.asOfDate}
            onChange={(e) => {
              if (e.target.value >= "0002-01-01")
                update({ asOfDate: e.target.value });
            }}
          />
        </div>
        <div className="min-w-52 space-y-1.5">
          <label
            htmlFor="attorneys-role"
            className="text-muted-foreground text-xs font-semibold tracking-wide uppercase"
          >
            Matter role
          </label>
          <ReportRoleDropdown
            id="attorneys-role"
            roles={data?.roles ?? []}
            selectedRoleId={selectedRoleId}
            pinnedRoleId={data?.pinnedRoleId}
            canPin={Boolean(
              data?.canManagePinnedRole &&
                data.pinnedRoleId !== undefined &&
                data.firmDateModified !== undefined,
            )}
            isPinning={isPinning}
            onSelect={(roleId) => {
              update({ roleId });
            }}
            onPin={onPinRole}
          />
        </div>
        <Button
          variant="outline"
          className="h-10 rounded-md px-3"
          onClick={onReset}
        >
          Reset filters
        </Button>
      </div>
      <span role="status" aria-live="polite" className="sr-only">
        {isFetching ? "Updating report" : "Report updated"}
      </span>
      {isError ? (
        <div role="alert" className="py-8 text-sm">
          The report could not be loaded.{" "}
          <button className="underline" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : (
        <>
          {data &&
            (!data.freshness.ready ||
              data.freshness.pendingMatters > 0 ||
              data.freshness.missingMatters > 0) && (
              <div
                role="status"
                className="border-b bg-amber-50 px-5 py-3 text-sm text-amber-950"
              >
                Reporting is updating. {data.freshness.missingMatters} matters
                await complete publication and {data.freshness.pendingMatters}{" "}
                have pending changes. These figures may be incomplete.
              </div>
            )}
          <section>
            <h2 className="mb-4 text-2xl font-semibold tracking-tight">
              {role ? `${role.label} summary` : "Assigned role summary"}
            </h2>
            <ReportTable
              columns={attorneyColumns}
              rows={data?.groups ?? []}
              total={data?.totals}
              rowKey={(r) => r.userId ?? "total"}
              isLoading={isLoading}
              tableName={tanstackTableNames.reportsAttorneySummary}
            />
          </section>
        </>
      )}
    </div>
  );
}
